import { useEffect, useRef, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronLeft, faChevronRight, faScissors } from '@fortawesome/free-solid-svg-icons';
import PdfPageCanvas from './PdfPageCanvas';

const MAX_SEAM_PAGE_WIDTH = 720;
// Border and padding on the page card, so the canvas fits the slot without growing it.
const SEAM_PAGE_CHROME = 22;

const SeamPage = ({ pageNumber, pdfDoc, width, excluded, segmentIndex, onToggleExclude, onOpenPreview }) => (
  <div
    className={`pdf-splitter-seam-page ${excluded ? 'pdf-splitter-page-excluded' : `pdf-splitter-seg-${segmentIndex % 6}`}`}
    role="button"
    tabIndex={0}
    title={`Click to enlarge page ${pageNumber}`}
    onClick={() => onOpenPreview(pageNumber)}
    onKeyDown={(event) => {
      if ((event.key === 'Enter' || event.key === ' ') && event.target === event.currentTarget) {
        event.preventDefault();
        onOpenPreview(pageNumber);
      }
    }}
  >
    <label
      className="pdf-splitter-page-checkbox"
      onClick={(event) => event.stopPropagation()}
      title={excluded ? `Include page ${pageNumber}` : `Discard page ${pageNumber}`}
    >
      <input
        type="checkbox"
        className="config-setting-checkbox"
        checked={!excluded}
        onChange={() => onToggleExclude(pageNumber)}
      />
    </label>
    {width > 0 && (
      <PdfPageCanvas pdfDoc={pdfDoc} pageNumber={pageNumber} width={width} />
    )}
  </div>
);

const PdfSplitterSeam = ({
  pdfDoc,
  numPages,
  leftPage,
  rightPage,
  leftExcluded,
  rightExcluded,
  leftSegmentIndex,
  rightSegmentIndex,
  isCut,
  onToggleCut,
  onToggleExclude,
  onPrevious,
  onNext,
  onOpenPreview,
}) => {
  const slotRef = useRef(null);
  const [slotWidth, setSlotWidth] = useState(0);

  useEffect(() => {
    const node = slotRef.current;
    if (!node) return undefined;
    const measure = () => {
      const style = window.getComputedStyle(node);
      const pad = parseFloat(style.paddingLeft) + parseFloat(style.paddingRight);
      const measured = Math.floor(node.clientWidth - pad) - SEAM_PAGE_CHROME;
      if (measured <= 0) return;
      const next = Math.min(measured, MAX_SEAM_PAGE_WIDTH);
      setSlotWidth((prev) => (prev === next ? prev : next));
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(node);
    return () => observer.disconnect();
  }, [rightPage]);

  return (
    <div className="pdf-splitter-seam">
      <button
        type="button"
        className="pdf-splitter-seam-nav"
        onClick={onPrevious}
        disabled={leftPage <= 1}
        title="Previous boundary"
      >
        <FontAwesomeIcon icon={faChevronLeft} />
      </button>

      <div className="pdf-splitter-seam-stage">
        <div className="pdf-splitter-seam-slot">
          <div className="pdf-splitter-seam-scroll" ref={slotRef}>
            <SeamPage
              pageNumber={leftPage}
              pdfDoc={pdfDoc}
              width={slotWidth}
              excluded={leftExcluded}
              segmentIndex={leftSegmentIndex}
              onToggleExclude={onToggleExclude}
              onOpenPreview={onOpenPreview}
            />
          </div>
          <div className={`pdf-splitter-seam-dock ${leftExcluded ? 'pdf-splitter-page-label-excluded' : ''}`}>
            Page {leftPage}
          </div>
        </div>

        {rightPage != null && (
          <button
            type="button"
            className={`pdf-splitter-cut-button pdf-splitter-seam-cut ${isCut ? 'pdf-splitter-cut-active' : ''}`}
            onClick={onToggleCut}
            aria-pressed={isCut}
            title={isCut ? `Remove split after page ${leftPage}` : `Split after page ${leftPage}`}
          >
            <FontAwesomeIcon icon={faScissors} />
          </button>
        )}

        {rightPage != null && (
          <div className="pdf-splitter-seam-slot">
            <div className="pdf-splitter-seam-scroll">
              <SeamPage
                pageNumber={rightPage}
                pdfDoc={pdfDoc}
                width={slotWidth}
                excluded={rightExcluded}
                segmentIndex={rightSegmentIndex}
                onToggleExclude={onToggleExclude}
                onOpenPreview={onOpenPreview}
              />
            </div>
            <div className={`pdf-splitter-seam-dock ${rightExcluded ? 'pdf-splitter-page-label-excluded' : ''}`}>
              Page {rightPage}
            </div>
          </div>
        )}
      </div>

      <button
        type="button"
        className="pdf-splitter-seam-nav"
        onClick={onNext}
        disabled={leftPage >= numPages}
        title="Next boundary"
      >
        <FontAwesomeIcon icon={faChevronRight} />
      </button>
    </div>
  );
};

export default PdfSplitterSeam;
